import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Logo } from '../ui'
import {
  RESET_PASSWORD_MUTATION,
  type UserPasswordResetResponse,
  type UserPasswordResetInput,
} from '../../graphql/mutations/passwordReset'
import { ROOT_PATH } from '../../config/rootPath'

interface PasswordResetFormPageProps {
  token: string
}

export function PasswordResetFormPage({ token }: PasswordResetFormPageProps) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const [resetPassword, { loading: _mutationLoading }] = useMutation<UserPasswordResetResponse, { input: UserPasswordResetInput }>(
    RESET_PASSWORD_MUTATION,
    {
      onCompleted: (data) => {
        if (data.userPasswordReset.success) {
          setSuccessMessage('Password reset successfully')
        } else if (data.userPasswordReset.errors && data.userPasswordReset.errors.length > 0) {
          setFieldErrors({ password: data.userPasswordReset.errors.join(', ') })
        }
        setIsSubmitting(false)
      },
      onError: (_error) => {
        setFieldErrors({ password: 'Unable to reset password. Please try again later.' })
        setIsSubmitting(false)
      },
    }
  )

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const errors: Record<string, string> = {}

    if (!password.trim()) {
      errors.password = 'New password is required'
    }
    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Confirm password is required'
    }

    setFieldErrors(errors)
    
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true)
      
      try {
        await resetPassword({
          variables: {
            input: {
              resetPasswordToken: token,
              password: password.trim(),
              passwordConfirmation: confirmPassword.trim()
            }
          }
        })
      } catch (error) {
        setFieldErrors({ password: 'An unexpected error occurred' })
        setIsSubmitting(false)
      }
    }
  }

  return (
    <>
      <div className="relative flex min-h-screen shrink-0 justify-center md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-white px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <main className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            <div className="flex">
              <a aria-label="Home" href={ROOT_PATH}>
                <Logo variant="image" showText={true} size="md" />
              </a>
            </div>
            <h2 className="mt-20 text-lg font-semibold text-gray-900">Reset your password</h2>
            <p className="mt-2 text-sm text-gray-700">
              Enter your new password below. Make sure it is at least 8 characters long and is something secure.
            </p>

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-md mt-4">
                <p className="text-sm font-medium">{successMessage}</p>
              </div>
            )}
            
            <form className="mt-10 grid grid-cols-1 gap-y-8" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="password" className="mb-3 block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="password"
                />
                {fieldErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                )}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="mb-3 block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="confirmPassword"
                />
                {fieldErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                )}
              </div>
              <div>
                <button
                  className="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600 w-full"
                  type="submit"
                  disabled={isSubmitting}
                >
                  <span>
                    {isSubmitting ? 'Resetting password...' : 'Reset password'} <span aria-hidden="true">→</span>
                  </span>
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <a href="/login" className="text-sm text-blue-600 hover:underline">
                Back to login
              </a>
            </div>
          </main>
        </div>
        <div className="hidden sm:contents lg:relative lg:block lg:flex-1">
          <img
            alt=""
            loading="lazy"
            width="1664"
            height="1866"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ color: 'transparent' }}
            src="/background-auth.jpg"
          />
        </div>
      </div>
    </>
  )
} 